import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CreditCard,
  Download,
  Upgrade,
  Warning,
  CheckCircle,
  Cancel as CancelIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  DeleteForever as DeleteIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { billingService, SubscriptionInfo, Invoice, PlanInfo } from '../services/billingService';
import { organizationService } from '../services/organizationService';

interface BillingDashboardProps {
  orgSlug: string;
  isOwner?: boolean;
}

const BillingDashboard: React.FC<BillingDashboardProps> = ({ orgSlug, isOwner = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [downgradeDialogOpen, setDowngradeDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [selectedBillingInterval, setSelectedBillingInterval] = useState<'MONTHLY' | 'YEARLY'>('MONTHLY');
  const [loading, setLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [availablePlans, setAvailablePlans] = useState<PlanInfo[]>([]);

  useEffect(() => {
    if (orgSlug) {
      const isSuccess = searchParams.get('success') === 'true';
      const isCanceled = searchParams.get('canceled') === 'true';

      if (isSuccess) {
        setSuccessMessage('Payment successful! Your subscription has been updated.');
        setSearchParams({ tab: 'billing' });
      } else if (isCanceled) {
        setError('Payment was canceled. You can try again when ready.');
        setSearchParams({ tab: 'billing' });
      }

      loadBillingData();
    }
  }, [orgSlug]);

  const loadBillingData = async (showLoading = true) => {
    if (!orgSlug) return;

    if (showLoading) {
      setLoading(true);
    }
    try {
      // Load subscription info and billing history in parallel
      const [subscriptionData, invoiceData, plansData] = await Promise.all([
        billingService.getSubscriptionInfo(orgSlug),
        billingService.getBillingHistory(orgSlug),
        billingService.getAvailablePlans(),
      ]);

      setSubscriptionInfo(subscriptionData);
      setInvoices(invoiceData);
      setAvailablePlans(plansData);
      setSelectedBillingInterval(subscriptionData.billingInterval || 'MONTHLY');
    } catch (err: any) {
      console.error('Failed to load billing data:', err);
      const errorData = err.response?.data?.error;
      const errorMsg = typeof errorData === 'string' ? errorData : errorData?.message || 'Failed to load billing information';
      setError(errorMsg);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'success';
      case 'past_due': return 'warning';
      case 'canceled': return 'error';
      default: return 'default';
    }
  };

  const getTierDisplayName = (tier: string) => {
    switch (tier) {
      case 'STARTER': return 'Starter';
      case 'FAMILY': return 'Family';
      case 'TEAM': return 'Team';
      default: return tier;
    }
  };

  const toSentenceCase = (str: string) => {
    const formatted = str.replace(/_/g, ' ').toLowerCase();
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const formatLimit = (limit: number) => {
    return limit === -1 ? '∞' : limit.toString();
  };

  const getUsagePercentage = (used: number, limit: number) => {
    if (limit === -1) return 0; // Unlimited
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'primary';
  };

  const handleUpgrade = (planId: string) => {
    setSelectedPlan(planId);
    setUpgradeDialogOpen(true);
  };

  const handleConfirmUpgrade = async () => {
    if (!selectedPlan) return;

    // Handle downgrade to Starter separately
    if (selectedPlan === 'STARTER') {
      setDowngradeDialogOpen(true);
      return;
    }

    // For paid plans, go directly to Stripe checkout
    setUpgradeLoading(true);
    setError(null);

    try {
      const { url } = await billingService.createCheckoutSession(orgSlug, selectedPlan, selectedBillingInterval);
      window.location.href = url;
    } catch (err: any) {
      const errorCode = err.response?.data?.code;
      const errorData = err.response?.data?.error;
      const errorMessage = typeof errorData === 'string' ? errorData : errorData?.message || 'Failed to create checkout session';

      if (errorCode === 'STRIPE_NOT_CONFIGURED') {
        setError('Payment processing is not available. Please contact support.');
      } else if (errorCode === 'PRICE_NOT_CONFIGURED') {
        setError('This pricing plan is not yet available. Please contact support.');
      } else {
        setError(errorMessage);
      }
      setUpgradeLoading(false);
    }
  };

  const handleConfirmDowngrade = async () => {
    setUpgradeLoading(true);
    setError(null);

    try {
      const isGiftedOnly = subscriptionInfo?.manualOverride && !subscriptionInfo?.stripeCustomerId;
      await billingService.cancelSubscription(orgSlug);
      setDowngradeDialogOpen(false);
      setUpgradeDialogOpen(false);
      if (isGiftedOnly) {
        // Gifted upgrade with no Stripe subscription — downgrade is immediate
        await loadBillingData(false);
        setSuccessMessage('Your plan has been downgraded to Starter.');
      } else {
        // Paid subscription — cancellation pending at period end
        if (subscriptionInfo) {
          setSubscriptionInfo({
            ...subscriptionInfo,
            cancelAtPeriodEnd: true,
          });
        }
        setSuccessMessage('Your subscription will be downgraded at the end of your billing period.');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error;
      setError(typeof errorMsg === 'string' ? errorMsg : errorMsg?.message || 'Failed to downgrade subscription');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    setUpgradeLoading(true);
    setError(null);

    try {
      const isGiftedOnly = subscriptionInfo?.manualOverride && !subscriptionInfo?.stripeCustomerId;
      await billingService.cancelSubscription(orgSlug);
      setCancelDialogOpen(false);
      if (isGiftedOnly) {
        await loadBillingData(false);
        setSuccessMessage('Your plan has been downgraded to Starter.');
      } else {
        if (subscriptionInfo) {
          setSubscriptionInfo({
            ...subscriptionInfo,
            cancelAtPeriodEnd: true,
          });
        }
        setSuccessMessage('Your subscription will be canceled at the end of your billing period.');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error;
      setError(typeof errorMsg === 'string' ? errorMsg : errorMsg?.message || 'Failed to cancel subscription');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    setUpgradeLoading(true);
    setError(null);

    try {
      await organizationService.deleteOrganization(orgSlug);
      // Redirect to home or organizations list after deletion
      window.location.href = '/';
    } catch (err: any) {
      const errorMsg = err.response?.data?.error;
      setError(typeof errorMsg === 'string' ? errorMsg : errorMsg?.message || 'Failed to delete organization');
      setUpgradeLoading(false);
    }
  };

  const handleManagePayment = async () => {
    setLoading(true);
    setError(null);

    try {
      const { url } = await billingService.createPortalSession(orgSlug);
      window.location.href = url;
    } catch (err: any) {
      const errorCode = err.response?.data?.code;
      const errorData = err.response?.data?.error;
      const errorMessage = typeof errorData === 'string' ? errorData : errorData?.message || 'Failed to open billing portal';

      // Show user-friendly messages based on error code
      if (errorCode === 'STRIPE_NOT_CONFIGURED') {
        setError('Payment management is not available. Please contact support.');
      } else if (errorCode === 'NO_BILLING_EMAIL') {
        setError('Please add a contact email to your organization before managing billing.');
      } else {
        setError(errorMessage);
      }
      setLoading(false);
    }
  };

  const handleDownloadInvoice = (invoice: Invoice) => {
    if (invoice.downloadUrl) {
      window.open(invoice.downloadUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !subscriptionInfo) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!subscriptionInfo) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No subscription information available
      </Alert>
    );
  }

  return (
    <Box>
      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Current Subscription */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Subscription
          </Typography>
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Chip
                  label={`${getTierDisplayName(subscriptionInfo.tier)} Plan`}
                  size="medium"
                  sx={{
                    bgcolor: 'white',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
                <Chip
                  label={toSentenceCase(subscriptionInfo.status)}
                  color={getStatusColor(subscriptionInfo.status) as any}
                  icon={subscriptionInfo.status === 'active' ? <CheckCircle /> : <Warning />}
                />
              </Box>
              
              {subscriptionInfo.nextBillingDate && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Next billing date: {new Date(subscriptionInfo.nextBillingDate).toLocaleDateString()}
                </Typography>
              )}
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                ${subscriptionInfo.amount.toFixed(2)}/{subscriptionInfo.billingInterval === 'YEARLY' ? 'year' : 'month'}
              </Typography>
              {subscriptionInfo.manualOverride && (
                <Box sx={{ width: { xs: '100%', sm: 376 }, mb: 2 }}>
                  <Alert severity="info">
                    Complimentary {getTierDisplayName(subscriptionInfo.manualOverride.tier)} access
                    {subscriptionInfo.manualOverride.expiresAt
                      ? ` until ${new Date(subscriptionInfo.manualOverride.expiresAt).toLocaleDateString()}`
                      : ' (no expiration)'}
                  </Alert>
                </Box>
              )}

              {subscriptionInfo.cancelAtPeriodEnd && subscriptionInfo.nextBillingDate && (
                <Box sx={{ width: { xs: '100%', sm: 376 }, mb: 2 }}>
                  <Alert severity="warning">
                    Your subscription will be canceled on {new Date(subscriptionInfo.nextBillingDate).toLocaleDateString()}.
                    You'll be downgraded to the Starter plan after this date.
                  </Alert>
                </Box>
              )}

              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                <Button
                  variant="contained"
                  startIcon={<Upgrade />}
                  onClick={() => handleUpgrade('CLUB')}
                  sx={{ width: { xs: '100%', sm: 160 }, flexShrink: 0 }}
                >
                  Change Plan
                </Button>
                <Button
                  variant="outlined"
                  startIcon={loading ? <CircularProgress size={20} /> : <CreditCard />}
                  onClick={handleManagePayment}
                  disabled={loading}
                  sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}
                >
                  {loading ? 'Loading...' : 'Manage Payment'}
                </Button>
              </Box>

              {error && !upgradeDialogOpen && !downgradeDialogOpen && !cancelDialogOpen && !deleteDialogOpen && (
                <Box sx={{ width: { xs: '100%', sm: 376 }, mt: 2 }}>
                  <Alert severity="error">
                    {error}
                  </Alert>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Usage Limits */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Usage & Limits
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Players
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <Typography variant="h6">
                    {subscriptionInfo.usageLimits.players.used} / {formatLimit(subscriptionInfo.usageLimits.players.limit)}
                  </Typography>
                  {getUsagePercentage(subscriptionInfo.usageLimits.players.used, subscriptionInfo.usageLimits.players.limit) >= 90 && (
                    <Warning color="warning" fontSize="small" />
                  )}
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={getUsagePercentage(subscriptionInfo.usageLimits.players.used, subscriptionInfo.usageLimits.players.limit)}
                  color={getUsageColor(getUsagePercentage(subscriptionInfo.usageLimits.players.used, subscriptionInfo.usageLimits.players.limit)) as any}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Leagues
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <Typography variant="h6">
                    {subscriptionInfo.usageLimits.leagues.used} / {formatLimit(subscriptionInfo.usageLimits.leagues.limit)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={getUsagePercentage(subscriptionInfo.usageLimits.leagues.used, subscriptionInfo.usageLimits.leagues.limit)}
                  color={getUsageColor(getUsagePercentage(subscriptionInfo.usageLimits.leagues.used, subscriptionInfo.usageLimits.leagues.limit)) as any}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Invoice History */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Invoice History
          </Typography>
          
          {/* Desktop Table View */}
          <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>
                      {new Date(invoice.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      ${invoice.amount.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={toSentenceCase(invoice.status)}
                        color={getStatusColor(invoice.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<Download />}
                        onClick={() => handleDownloadInvoice(invoice)}
                        disabled={!invoice.downloadUrl}
                      >
                        Download
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {invoices.map((invoice) => (
              <Card key={invoice.id} sx={{ mb: 2 }} variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        ${invoice.amount.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(invoice.date).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Chip
                      label={toSentenceCase(invoice.status)}
                      color={getStatusColor(invoice.status) as any}
                      size="small"
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download />}
                    onClick={() => handleDownloadInvoice(invoice)}
                    disabled={!invoice.downloadUrl}
                    fullWidth
                  >
                    Download Invoice
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Danger Zone - Cancel subscription for owner+admin, delete org for owner only */}
      {(subscriptionInfo.tier !== 'STARTER' || isOwner) && (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" color="error" gutterBottom>
            {subscriptionInfo.tier !== 'STARTER' ? 'Cancel Subscription' : 'Delete Organization'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subscriptionInfo.tier !== 'STARTER'
              ? isOwner
                ? 'Cancel your subscription and downgrade to Starter or delete your organization. Deleting your organization is permanent.'
                : 'Cancel the subscription and downgrade to Starter.'
              : 'Delete your organization. This action is permanent.'}
          </Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={subscriptionInfo.tier !== 'STARTER' ? <CancelIcon /> : <DeleteIcon />}
            onClick={() => subscriptionInfo.tier !== 'STARTER' ? setCancelDialogOpen(true) : setDeleteDialogOpen(true)}
          >
            {subscriptionInfo.tier !== 'STARTER' ? 'Cancel Subscription' : 'Delete Organization'}
          </Button>
        </CardContent>
      </Card>
      )}

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialogOpen} onClose={() => setUpgradeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SwapHorizIcon />Change Your Plan</Box></DialogTitle>
        <DialogContent>
          {/* Billing Interval Toggle */}
          <Box display="flex" justifyContent="center" sx={{ mb: 3 }}>
            <ToggleButtonGroup
              value={selectedBillingInterval}
              exclusive
              onChange={(_, value) => value && setSelectedBillingInterval(value)}
              aria-label="billing interval"
            >
              <ToggleButton value="MONTHLY" aria-label="monthly billing">
                Monthly
              </ToggleButton>
              <ToggleButton value="YEARLY" aria-label="yearly billing">
                Yearly (Save 33%)
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Grid container spacing={2}>
            {availablePlans.map((plan) => {
              const price = selectedBillingInterval === 'YEARLY' ? plan.yearlyPrice : plan.monthlyPrice;
              const isCurrentPlan = subscriptionInfo?.tier === plan.id;

              return (
                <Grid item xs={12} md={6} key={plan.id}>
                  <Card
                    variant={selectedPlan === plan.id ? 'outlined' : 'elevation'}
                    sx={{
                      cursor: 'pointer',
                      border: selectedPlan === plan.id ? 2 : 1,
                      borderColor: selectedPlan === plan.id ? 'primary.main' : 'divider',
                      height: '100%',
                    }}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="h6">
                          {plan.name}
                        </Typography>
                        {isCurrentPlan && (
                          <Chip label="Current" size="small" color="primary" />
                        )}
                      </Box>
                      <Typography variant="h4" sx={{ mb: 0 }}>
                        {price === 0 ? 'Free' : `$${price.toFixed(2)}`}
                        {price > 0 && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            /{selectedBillingInterval === 'YEARLY' ? 'year' : 'month'}
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 0.5, minHeight: '1.2em', visibility: selectedBillingInterval === 'YEARLY' && plan.monthlyPrice > 0 ? 'visible' : 'hidden' }}>
                        Save ${((plan.monthlyPrice * 12) - plan.yearlyPrice).toFixed(2)}/year
                      </Typography>
                      <Box>
                        {plan.features.map((feature, index) => (
                          <Typography key={index} variant="body2" color="text.secondary">
                            • {feature}
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUpgradeDialogOpen(false); setError(null); }} variant="outlined" startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmUpgrade}
            disabled={!selectedPlan || upgradeLoading}
            endIcon={upgradeLoading ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
          >
            {upgradeLoading ? 'Processing...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Downgrade Confirmation Dialog */}
      <Dialog open={downgradeDialogOpen} onClose={() => setDowngradeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><ArrowDownwardIcon />Downgrade to Starter</Box></DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to downgrade to the free Starter plan?
          </Alert>
          <Typography variant="body1" gutterBottom>
            This will:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1 }}>
            {subscriptionInfo?.manualOverride && !subscriptionInfo?.stripeCustomerId ? (
              <>
                <li>
                  <Typography variant="body2">Remove your complimentary {getTierDisplayName(subscriptionInfo.manualOverride.tier)} access</Typography>
                </li>
                <li>
                  <Typography variant="body2">Remove access to premium features (weather, travel conflicts, departure times, analytics)</Typography>
                </li>
                <li>
                  <Typography variant="body2">Take effect immediately</Typography>
                </li>
              </>
            ) : (
              <>
                <li>
                  <Typography variant="body2">Cancel your current subscription</Typography>
                </li>
                <li>
                  <Typography variant="body2">Remove access to premium features (weather, travel conflicts, departure times, analytics)</Typography>
                </li>
                <li>
                  <Typography variant="body2">Take effect at the end of your current billing period</Typography>
                </li>
              </>
            )}
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDowngradeDialogOpen(false)} variant="outlined" startIcon={<CancelIcon />} disabled={upgradeLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmDowngrade}
            disabled={upgradeLoading}
            startIcon={upgradeLoading ? <CircularProgress size={20} color="inherit" /> : <ArrowDownwardIcon />}
          >
            {upgradeLoading ? 'Processing...' : 'Confirm Downgrade'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Warning />Cancel Subscription</Box></DialogTitle>
        <DialogContent>
          <Typography variant="body1" fontWeight="medium" gutterBottom>
            Choose an option:
          </Typography>

          {/* Option 1: Downgrade to Starter */}
          <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Downgrade to Starter (Free)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keep your organization, players, and leagues. You'll lose access to premium features:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0, mb: 2 }}>
              <li><Typography variant="body2">No weather, travel-aware conflicts, or departure times</Typography></li>
              <li><Typography variant="body2">No email digests or league analytics</Typography></li>
              <li><Typography variant="body2">{subscriptionInfo?.manualOverride && !subscriptionInfo?.stripeCustomerId ? 'Takes effect immediately' : 'Takes effect at end of billing period'}</Typography></li>
            </Box>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleConfirmCancel}
              disabled={upgradeLoading}
              startIcon={upgradeLoading ? <CircularProgress size={20} /> : <ArrowDownwardIcon />}
              fullWidth
            >
              {upgradeLoading ? 'Processing...' : 'Downgrade to Starter'}
            </Button>
          </Card>

          {/* Option 2: Delete Organization (owner only) */}
          {isOwner && (
          <Card variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium" color="error" gutterBottom>
              Delete Organization
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Deleting your organization is permanent and cannot be undone.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This will permanently delete:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0, mb: 2 }}>
              <li><Typography variant="body2">All players, teams, and leagues</Typography></li>
              <li><Typography variant="body2">All historical records and files</Typography></li>
            </Box>
            <Button
              variant="outlined"
              color="error"
              onClick={() => { setCancelDialogOpen(false); setDeleteDialogOpen(true); }}
              disabled={upgradeLoading}
              startIcon={<DeleteIcon />}
              fullWidth
            >
              Delete Organization
            </Button>
          </Card>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} variant="contained" startIcon={<CheckCircle />} disabled={upgradeLoading}>
            Keep Subscription
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Organization Confirmation Dialog (owner only) */}
      {isOwner && <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><DeleteIcon />Delete Organization</Box></DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action is permanent and cannot be undone!
          </Alert>
          <Typography variant="body1" gutterBottom>
            Deleting this organization will permanently remove:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1 }}>
            <li><Typography variant="body2">All players and their data</Typography></li>
            <li><Typography variant="body2">All teams and leagues</Typography></li>
            <li><Typography variant="body2">All historical records and statistics</Typography></li>
            <li><Typography variant="body2">All files and media</Typography></li>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined" startIcon={<CancelIcon />} disabled={upgradeLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={upgradeLoading}
            startIcon={upgradeLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {upgradeLoading ? 'Deleting...' : 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>}
    </Box>
  );
};

export default BillingDashboard;
